import * as XLSX from "xlsx"
import JSZip from "jszip"
import type {
  MeeshoPaymentFile,
  OrderRow,
  AdRow,
  ReferralRow,
  CompensationRow,
  CsvOrderRow
} from "@/features/profit-calculators/calculator-shared/types"

const toNum = (v: unknown): number => {
  if (v === null || v === undefined || v === "") return 0
  const n = parseFloat(String(v).replace(/[^\d.-]/g, ""))
  return isNaN(n) ? 0 : n
}

const toStr = (v: unknown): string => (v === null || v === undefined ? "" : String(v).trim())

// ─── XLSX parsing ────────────────────────────────────────────────────────────

function parseOrderPayments(ws: XLSX.WorkSheet): OrderRow[] {
  const rows: OrderRow[] = []
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1:A1")

  // Row 0 = merged header group, Row 1 = column headers, Row 2 = formula description string → skip
  // Data starts at sheet row 4 (0-indexed: r=3)
  for (let r = 3; r <= range.e.r; r++) {
    const cell = (col: number) => {
      const ref = XLSX.utils.encode_cell({ r, c: col })
      return ws[ref]?.v
    }
    const status = toStr(cell(7))
    if (!cell(0) && !status) continue

    rows.push({
      subOrderNo: toStr(cell(0)),
      orderDate: toStr(cell(1)),
      dispatchDate: toStr(cell(2)),
      productName: toStr(cell(3)),
      supplierSKU: toStr(cell(4)),
      catalogId: toStr(cell(5)),
      orderSource: toStr(cell(6)),
      liveOrderStatus: status,
      productGSTPct: toNum(cell(8)),
      listingPrice: toNum(cell(9)),
      quantity: toNum(cell(10)) || 1,
      transactionId: toStr(cell(11)),
      paymentDate: toStr(cell(12)),
      finalSettlementAmount: toNum(cell(13)), // col N
      priceType: toStr(cell(14)),
      totalSaleAmount: toNum(cell(15)), // col P
      totalSaleReturnAmount: toNum(cell(16)), // col Q
      fixedFeeRevenue: toNum(cell(17)),
      warehousingFeeRevenue: toNum(cell(18)),
      returnPremium: toNum(cell(19)),
      returnPremiumOfReturn: toNum(cell(20)),
      meeshoCommissionPct: toNum(cell(21)),
      meeshoCommission: toNum(cell(22)), // col W
      meeshoGoldPlatformFee: toNum(cell(23)),
      meeshoMallPlatformFee: toNum(cell(24)),
      fixedFeeDeduction: toNum(cell(25)),
      warehousingFeeDeduction: toNum(cell(26)),
      returnShippingCharge: toNum(cell(27)), // col AB
      gstCompensation: toNum(cell(28)),
      shippingCharge: toNum(cell(29)), // col AD
      otherSupportCharges: toNum(cell(30)),
      waivers: toNum(cell(31)),
      netOtherSupportCharges: toNum(cell(32)),
      gstOnNetOtherSupport: toNum(cell(33)),
      tcs: toNum(cell(34)),
      tdsRatePct: toNum(cell(35)),
      tds: toNum(cell(36)),
      compensation: toNum(cell(37)),
      claims: toNum(cell(38)),
      recovery: toNum(cell(39)),
      compensationReason: toStr(cell(40)),
      claimsReason: toStr(cell(41)),
      recoveryReason: toStr(cell(42))
    })
  }
  return rows
}

function parseAds(ws: XLSX.WorkSheet): AdRow[] {
  const rows: AdRow[] = []
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1:A1")
  for (let r = 2; r <= range.e.r; r++) {
    const cell = (col: number) => ws[XLSX.utils.encode_cell({ r, c: col })]?.v
    if (!cell(0) && !cell(2)) continue
    const firstVal = toStr(cell(0))
    if (firstVal.toLowerCase().includes("no data")) continue
    rows.push({
      deductionDuration: firstVal,
      deductionDate: toStr(cell(1)),
      campaignId: toStr(cell(2)),
      adCost: toNum(cell(3)),
      creditsWaivers: toNum(cell(4)),
      adCostInclCredits: toNum(cell(5)),
      gst: toNum(cell(6)),
      totalAdsCost: toNum(cell(7))
    })
  }
  return rows
}

function parseReferrals(ws: XLSX.WorkSheet): ReferralRow[] {
  const rows: ReferralRow[] = []
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1:A1")
  for (let r = 2; r <= range.e.r; r++) {
    const cell = (col: number) => ws[XLSX.utils.encode_cell({ r, c: col })]?.v
    if (!cell(0)) continue
    const firstVal = toStr(cell(0))
    if (firstVal.toLowerCase().includes("no data")) continue
    rows.push({
      rewardId: firstVal,
      paymentDate: toStr(cell(1)),
      storeName: toStr(cell(2)),
      reason: toStr(cell(3)),
      netReferralAmount: toNum(cell(4)),
      taxes: toNum(cell(5))
    })
  }
  return rows
}

function parseCompensations(ws: XLSX.WorkSheet): CompensationRow[] {
  const rows: CompensationRow[] = []
  const range = XLSX.utils.decode_range(ws["!ref"] ?? "A1:A1")
  for (let r = 2; r <= range.e.r; r++) {
    const cell = (col: number) => ws[XLSX.utils.encode_cell({ r, c: col })]?.v
    if (!cell(0)) continue
    const firstVal = toStr(cell(0))
    if (firstVal.toLowerCase().includes("no data")) continue
    rows.push({
      date: firstVal,
      programName: toStr(cell(1)),
      reason: toStr(cell(2)),
      amount: toNum(cell(3))
    })
  }
  return rows
}

// ─── Period label extraction ──────────────────────────────────────────────────

function extractPeriodLabel(fileName: string): string {
  const match = fileName.match(/(\d{4}-\d{2}-\d{2})_(\d{4}-\d{2}-\d{2})/)
  if (match) {
    const fmt = (d: string) => {
      const [y, m, day] = d.split("-")
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]
      return `${parseInt(day)} ${months[parseInt(m) - 1]} ${y}`
    }
    return `${fmt(match[1])} – ${fmt(match[2])}`
  }
  return fileName.replace(/\.[^.]+$/, "")
}

// ─── Public API ───────────────────────────────────────────────────────────────

export async function parsePaymentFile(file: File): Promise<MeeshoPaymentFile> {
  const isCsv = file.name.toLowerCase().endsWith(".csv")

  if (isCsv) {
    // HIGH SPEED PARSING: Use SheetJS binary array-buffer parsing to parse CSV contents
    const buffer = await file.arrayBuffer()
    const wb = XLSX.read(buffer, { type: "array", raw: true })
    const ws = wb.Sheets[wb.SheetNames[0]]
    if (!ws) throw new Error("CSV file is empty.")

    const data = XLSX.utils.sheet_to_json<any[]>(ws, { header: 1 })
    if (data.length < 2) {
      return {
        fileName: file.name,
        periodLabel: extractPeriodLabel(file.name),
        fileType: "csv",
        orders: [],
        ads: [],
        referrals: [],
        compensations: [],
        csvOrders: []
      }
    }

    const headers = (data[0] || []).map(h => String(h).trim())
    const getIdx = (partial: string) => headers.findIndex(h => h.toLowerCase().includes(partial.toLowerCase()))

    const idxStatus = getIdx("Reason for Credit")
    const idxSubOrder = getIdx("Sub Order")
    const idxCatalog = getIdx("Catalog ID")
    const idxOrderDate = getIdx("Order Date")
    const idxSource = getIdx("Order source")
    const idxState = getIdx("Customer State")
    const idxProduct = getIdx("Product Name")
    const idxSku = getIdx("SKU")
    const idxSize = getIdx("Size")
    const idxQty = getIdx("Quantity")
    const idxListedPrice = getIdx("Supplier Listed Price")
    const idxDiscPrice = getIdx("Supplier Discounted Price")
    const idxPacket = getIdx("Packet Id")

    const csvOrders: CsvOrderRow[] = []
    for (let i = 1; i < data.length; i++) {
      const cols = data[i]
      if (!cols || cols.length === 0) continue

      const get = (idx: number) =>
        idx >= 0 && cols[idx] !== undefined && cols[idx] !== null ? String(cols[idx]).trim() : ""

      const subOrderNo = get(idxSubOrder)
      const status = get(idxStatus)
      if (!status && !subOrderNo) continue

      csvOrders.push({
        reasonForCreditEntry: status,
        subOrderNo,
        catalogId: get(idxCatalog),
        orderDate: get(idxOrderDate),
        orderSource: get(idxSource),
        customerState: get(idxState),
        productName: get(idxProduct),
        sku: get(idxSku),
        size: get(idxSize),
        quantity: toNum(get(idxQty)) || 1,
        supplierListedPrice: toNum(get(idxListedPrice)),
        supplierDiscountedPrice: toNum(get(idxDiscPrice)),
        packetId: get(idxPacket)
      })
    }

    return {
      fileName: file.name,
      periodLabel: extractPeriodLabel(file.name),
      fileType: "csv",
      orders: [],
      ads: [],
      referrals: [],
      compensations: [],
      csvOrders
    }
  }

  const buffer = await file.arrayBuffer()
  const wb = XLSX.read(buffer, { type: "array" })

  const orderWs = wb.Sheets["Order Payments"]
  if (!orderWs) throw new Error(`File "${file.name}" does not contain an "Order Payments" sheet.`)

  return {
    fileName: file.name,
    periodLabel: extractPeriodLabel(file.name),
    fileType: "xlsx",
    orders: parseOrderPayments(orderWs),
    ads: wb.Sheets["Ads Cost"] ? parseAds(wb.Sheets["Ads Cost"]) : [],
    referrals: wb.Sheets["Referral Payments"] ? parseReferrals(wb.Sheets["Referral Payments"]) : [],
    compensations: wb.Sheets["Compensation and Recovery"]
      ? parseCompensations(wb.Sheets["Compensation and Recovery"])
      : [],
    csvOrders: []
  }
}

/** Extracts an Excel file from a ZIP archive and parses it */
export async function parsePaymentZip(file: File): Promise<MeeshoPaymentFile> {
  const zip = new JSZip()
  const loadedZip = await zip.loadAsync(file)
  const xlsxFile = Object.values(loadedZip.files).find(
    f => f.name.toLowerCase().endsWith(".xlsx") || f.name.toLowerCase().endsWith(".xls")
  )
  if (!xlsxFile) {
    throw new Error(`No Excel file (.xlsx/.xls) found inside ZIP archive "${file.name}".`)
  }
  const buffer = await xlsxFile.async("arraybuffer")
  const tempFile = new File([buffer], xlsxFile.name, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  })
  return parsePaymentFile(tempFile)
}

export {
  computeCombinedReport,
  getMeeshoMonthLabel,
  normalizeStatus
} from "@/features/profit-calculators/meesho-calculator/utils/meeshoReportCompiler"
export { computeSummary } from "@/features/profit-calculators/meesho-calculator/utils/summaryCompiler"
